import React, { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import { RouteComponentProps } from 'react-router-dom';

import {
  TYPE_PROJECT_INVITE,
  TYPE_GENERIC_HOUSEHOLD,
  TYPE_HOUSEHOLD_INVITE,
  TYPE_CONTACT_INVITE,
  TYPE_HOMEOWNER_REFERRAL_INVITE,
} from '../../config';

// utils
import { selectSignUpInvite } from '../../../../redux/auth/selectors';

// components
import Clickable from '../../../../components/Clickable';
import Button from '../../../../components/Button';
import StartingPromptModal from '../../../../components/StartingPrompt/StartingPromptModal';
import ValueProps from './components/ValueProps';

// styles
import containerStyles from './Instructions.scss';
import qmStyles from '../../../../theme/qmStyles';
import GenericSplash from './components/GenericSplash';

// copy
import { additionalInfo, additionalInfoCommunity } from './copyMap';
import QmImage from '../../../../components/Image/QmImage';
import JoinProperty from './components/JoinProperty';

const HOMEOWNER_REFERRAL = 'HOMEOWNER_REFERRAL';

interface Invite {
  starting_prompt?: string;
  community_invite: any;
  sender: any;
}

interface InstructionsProps extends RouteComponentProps<any> {
  navigateNext: () => void;
  type: string;
  inviteData?: Invite;
}

const Instructions: React.FC<InstructionsProps> = ({
  navigateNext,
  type,
  history,
}) => {
  const [promptModalOpen, setPromptModalOpen] = useState<boolean>(false);
  const [showAdditionalInfo, setShowAdditionalInfo] = useState<boolean>(false);
  const inviteData = useSelector(selectSignUpInvite);

  const getDisplayPrompt = () =>
    !!inviteData && !isEmpty(inviteData.starting_prompt);

  useEffect(
    () => {
      if (getDisplayPrompt()) {
        setPromptModalOpen(true);
      }
    },
    [inviteData],
  );

  useEffect(
    () => {
      setShowAdditionalInfo(
        type === TYPE_PROJECT_INVITE ||
          (type === TYPE_CONTACT_INVITE &&
            inviteData &&
            inviteData.community_invite),
      );
    },
    [type, inviteData],
  );

  const renderHomeownerReferral = () => {
    const { header } = inviteData;
    return (
      <div className={containerStyles.homeownerReferralContainer}>
        {!!header.image && (
          <QmImage
            src={header.image}
            height={100}
            width={100}
            imgStyles={{ borderRadius: 50 }}
            className="m-gutter"
          />
        )}
        {header.markdown_text && (
          <ReactMarkdown style={{ color: header.text_color }}>
            {header.markdown_text}
          </ReactMarkdown>
        )}
        {header.markdown_description && (
          <ReactMarkdown style={{ color: header.text_color }}>
            {header.markdown_description}
          </ReactMarkdown>
        )}
        <Button
          text="Claim Offer"
          onClick={navigateNext}
          className={containerStyles.button}
          style={{
            width: 360,
            backgroundColor: qmStyles.qmBabyGreen,
            color: qmStyles.white,
            margin: qmStyles.gutter,
          }}
        />
        <p className={containerStyles.homeownerReferralDescription}>
          Quartermaster helps our friends and neighbours find Home Improvement
          Pros each other have used and referred.
        </p>
        <p className={containerStyles.homeownerReferralDescription}>
          Help me build out our Pro referral network!
        </p>
      </div>
    );
  };

  // render fns
  const renderAdditionalInfo = () => (
    <div className={containerStyles.additionalInfoContainer}>
      <div className={containerStyles.additionalInfoPoint}>
        {(inviteData && inviteData.community_invite
          ? additionalInfoCommunity
          : additionalInfo
        ).map(point => (
          <div
            key={point.text}
            className={containerStyles.additionalInfoPointContainer}
          >
            <div
              className={`qm-icon-${point.icon} ${
                containerStyles.additionalInfoIcon
              }`}
            />
            <p className={containerStyles.additionalInfoText}>{point.text}</p>
          </div>
        ))}
      </div>
      <p className={containerStyles.additionalInfoDescription}>
        Sign up to join a community of 5000+ homeowners in Toronto!
      </p>
      <Button
        text="Continue"
        onClick={navigateNext}
        className={containerStyles.button}
        style={{
          width: 360,
          backgroundColor: qmStyles.qmGreen,
          color: qmStyles.white,
          marginBottom: qmStyles.doubleGutter,
        }}
      />
    </div>
  );

  const navigateSignIn = () => {
    history.push('/login');
  };

  const renderQuartermasterValueProps = () => <ValueProps />;

  const renderBoQModal = () => {
    if (!getDisplayPrompt()) {
      return null;
    }

    return (
      <StartingPromptModal
        isOpen={promptModalOpen}
        inviteData={inviteData}
        onNext={navigateNext}
      />
    );
  };

  const renderInstructionContent = () => {
    if (type === TYPE_GENERIC_HOUSEHOLD || type === TYPE_HOUSEHOLD_INVITE) {
      return <JoinProperty />;
    }

    if (type === TYPE_HOMEOWNER_REFERRAL_INVITE) {
      return renderHomeownerReferral();
    }

    return <GenericSplash />;
  };

  const renderPromotionTerms = () => (
    <span className={containerStyles.promoTermsText}>
      {inviteData.promotion_terms}
    </span>
  );

  return (
    <div className={containerStyles.container}>
      {renderInstructionContent()}
      {showAdditionalInfo && renderAdditionalInfo()}
      {renderBoQModal()}
      {!showAdditionalInfo && type !== TYPE_HOMEOWNER_REFERRAL_INVITE && (
        <React.Fragment>
          <Button
            text="Next"
            onClick={navigateNext}
            className={containerStyles.button}
            style={{
              width: 360,
              backgroundColor: qmStyles.qmBabyGreen,
              color: qmStyles.white,
              marginBottom: qmStyles.gutter,
            }}
          />
          <p className={containerStyles.accountText}>
            Already have an account?{' '}
            <Clickable
              className={containerStyles.hyperlink}
              onClick={navigateSignIn}
            >
              Sign In
            </Clickable>
          </p>
        </React.Fragment>
      )}
      {renderQuartermasterValueProps()}
      {inviteData && inviteData.invitation_type === HOMEOWNER_REFERRAL && (
        <div>
          <Button
            text="Claim Offer"
            onClick={navigateNext}
            className={containerStyles.button}
            style={{
              width: 360,
              backgroundColor: qmStyles.qmBabyGreen,
              color: qmStyles.white,
              margin: qmStyles.gutter,
            }}
          />
        </div>
      )}
      {inviteData && inviteData.promotion_terms && renderPromotionTerms()}
    </div>
  );
};

export default Instructions;
